import React, { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";

export function EnlargedImageOverlay({ images, initialIndex = 0, onClose, captions = [], totalCount = images.length, globalIndexOffset = 0  }) {
  const containerRef = useRef(null);
  const imgRef = useRef(null);
  const [scale, setScale] = useState(1);
  const [translate, setTranslate] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [lastPos, setLastPos] = useState({ x: 0, y: 0 });
  const [currentIndex, setCurrentIndex] = useState(initialIndex);

  // For pinch zoom tracking
  const lastTouch = useRef({ distance: null, midpoint: null });
  const globalIndex = globalIndexOffset + currentIndex + 1;

  useEffect(() => {
    setCurrentIndex(initialIndex);
  }, [initialIndex]);

  useEffect(() => {
    setScale(1);
    setTranslate({ x: 0, y: 0 });
  }, [currentIndex]);

  const clampTranslation = (newTranslate, scale) => {
    const container = containerRef.current;
    const image = imgRef.current;
    if (!container || !image) return newTranslate;

    const containerRect = container.getBoundingClientRect();
    const containerWidth = containerRect.width;
    const containerHeight = containerRect.height;

    const baseWidth = image.offsetWidth;
    const baseHeight = image.offsetHeight;

    const imageWidth = baseWidth * scale;
    const imageHeight = baseHeight * scale;

    const margin = 20;

    const maxX = Math.max(0, (imageWidth - containerWidth) / 2 - margin);
    const maxY = Math.max(0, (imageHeight - containerHeight) / 2 - margin);

    return {
      x: Math.min(maxX, Math.max(-maxX, newTranslate.x)),
      y: Math.min(maxY, Math.max(-maxY, newTranslate.y)),
    };
  };

  const handleWheel = (e) => {
    e.preventDefault();
    const delta = -e.deltaY;
    const zoomIntensity = 0.001;

    setScale((prev) => {
      const newScale = Math.min(10, Math.max(1, prev + delta * zoomIntensity));
      if (newScale <= 1) setTranslate({ x: 0, y: 0 });
      else setTranslate((prevTranslate) => clampTranslation(prevTranslate, newScale));
      return newScale;
    });
  };

  // Mouse events for desktop drag
  const handleMouseDown = (e) => {
    if (scale <= 1) return;
    setIsDragging(true);
    setLastPos({ x: e.clientX, y: e.clientY });
  };

  const handleMouseMove = (e) => {
    if (!isDragging || scale <= 1) return;
    const dx = e.clientX - lastPos.x;
    const dy = e.clientY - lastPos.y;
    setTranslate((prev) => clampTranslation({ x: prev.x + dx, y: prev.y + dy }, scale));
    setLastPos({ x: e.clientX, y: e.clientY });
  };

  const handleMouseUp = () => setIsDragging(false);

  // Touch events for mobile pinch zoom and drag
  const handleTouchStart = (e) => {
    if (e.touches.length === 2) {
      const [touch1, touch2] = e.touches;
      const dx = touch1.clientX - touch2.clientX;
      const dy = touch1.clientY - touch2.clientY;
      lastTouch.current = {
        distance: Math.hypot(dx, dy),
        midpoint: {
          x: (touch1.clientX + touch2.clientX) / 2,
          y: (touch1.clientY + touch2.clientY) / 2,
        },
      };
    } else if (e.touches.length === 1) {
      setLastPos({ x: e.touches[0].clientX, y: e.touches[0].clientY });
    }
  };

  const handleTouchMove = (e) => {
    if (e.touches.length === 2) {
      const [touch1, touch2] = e.touches;
      const dx = touch1.clientX - touch2.clientX;
      const dy = touch1.clientY - touch2.clientY;
      const newDistance = Math.hypot(dx, dy);

      const prevDistance = lastTouch.current.distance || newDistance;
      const delta = newDistance - prevDistance;
      lastTouch.current.distance = newDistance;

      const midpoint = {
        x: (touch1.clientX + touch2.clientX) / 2,
        y: (touch1.clientY + touch2.clientY) / 2,
      };

      setScale((prevScale) => {
        let newScale = Math.min(10, Math.max(1, prevScale + delta * 0.005));
        newScale = Number(newScale.toFixed(4)); // prevent floating precision noise

        const container = containerRef.current;
        if (!container) return newScale;
        const rect = container.getBoundingClientRect();

        const offsetX = midpoint.x - rect.left - rect.width / 2;
        const offsetY = midpoint.y - rect.top - rect.height / 2;

        setTranslate((prev) => {
          const scaleChange = newScale / prevScale;
          const newX = prev.x - offsetX * (scaleChange - 1);
          const newY = prev.y - offsetY * (scaleChange - 1);
          return clampTranslation({ x: newX, y: newY }, newScale);
        });

        return newScale;
      });
    } else if (e.touches.length === 1 && scale > 1) {
      const dx = e.touches[0].clientX - lastPos.x;
      const dy = e.touches[0].clientY - lastPos.y;
      setTranslate((prev) => clampTranslation({ x: prev.x + dx, y: prev.y + dy }, scale));
      setLastPos({ x: e.touches[0].clientX, y: e.touches[0].clientY });
    }
    e.preventDefault(); // prevent scrolling
  };

  useEffect(() => {
    const handleTouchEnd = () => {
      setIsDragging(false);
      setTranslate((prev) => clampTranslation(prev, scale));
      lastTouch.current = { distance: null, midpoint: null };
    };

    window.addEventListener("touchend", handleTouchEnd);
    window.addEventListener("touchcancel", handleTouchEnd);

    return () => {
      window.removeEventListener("touchend", handleTouchEnd);
      window.removeEventListener("touchcancel", handleTouchEnd);
    };
  }, [scale]);

  const goToPrevious = (e) => {
    e.stopPropagation();
    if (currentIndex > 0) {
      setCurrentIndex((prev) => prev - 1);
    }
  };

  const goToNext = (e) => {
    e.stopPropagation();
    if (currentIndex < images.length - 1) {
      setCurrentIndex((prev) => prev + 1);
    }
  };

  return createPortal(
    <div
      ref={containerRef}
      onWheel={handleWheel}
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseUp}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        width: "100vw",
        height: "100vh",
        backgroundColor: "rgba(0,0,0,0.9)",
        zIndex: 10000,
        overflow: "hidden",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        userSelect: "none",
        touchAction: "none",
        flexDirection: "column", // STACK children vertically
      }}
    >
      {/* Close Button */}
      <button
        onClick={(e) => {
          e.stopPropagation();
          onClose();
        }}
        style={{
          position: "absolute",
          top: 20,
          right: 20,
          fontSize: "28px",
          background: "transparent",
          border: "none",
          color: "#ccc",
          cursor: "pointer",
          zIndex: 10001,
        }}
        aria-label="Close enlarged image"
      >
        ×
      </button>

      {/* Image + Caption Container */}
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          maxWidth: "90vw",
          maxHeight: "90vh",
        }}
      >
        {/* Enlarged Image */}
        <img
          ref={imgRef}
          src={images[currentIndex]}
          alt={`Enlarged image ${currentIndex + 1}`}
          onClick={(e) => e.stopPropagation()}
          style={{
            transform: `translate(${translate.x}px, ${translate.y}px) scale(${scale})`,
            transition: isDragging ? "none" : "transform 0.2s ease-out",
            maxWidth: "90vw",
            maxHeight: "80vh",
            borderRadius: 8,
            userSelect: "none",
            pointerEvents: "auto",
            marginBottom: "1rem",
          }}
          draggable={false}
        />

        {/* Caption (only when not zoomed) */}
        {captions[currentIndex] && scale === 1 && (
          <div
            style={{
              color: "#ccc",
              backgroundColor: "rgba(0, 0, 0, 0.95)",
              padding: "0.4rem 1rem",
              borderRadius: 6,
              fontSize: "0.68rem",
              textAlign: "left",
              maxWidth: "88vw",
              maxHeight: "8rem",
              overflowY: "auto",
              marginTop: "0.2rem",
            }}
          >
            <strong>Fig {(1 + currentIndex * 0.1).toFixed(1)}.</strong>{" "}
            {captions[currentIndex]}
          </div>
        )}

        {/* Index Display */}
        {scale === 1 && (
          <div
            style={{
              color: "#bbb",
              fontSize: "14px",
              marginTop: "0.5rem",
            }}
          >
            {globalIndex} of {totalCount}
          </div>
        )}
      </div>

      {/* Navigation Arrows */}
      {currentIndex > 0 && (
        <button
          onClick={goToPrevious}
          style={{
            position: "absolute",
            left: "10px",
            top: "50%",
            transform: "translateY(-50%)",
            fontSize: "32px",
            background: "transparent",
            border: "none",
            color: "#ccc",
            cursor: "pointer",
            zIndex: 10001,
          }}
          aria-label="Previous image"
        >
          ‹
        </button>
      )}
      {currentIndex < images.length - 1 && (
        <button
          onClick={goToNext}
          style={{
            position: "absolute",
            right: "10px",
            top: "50%",
            transform: "translateY(-50%)",
            fontSize: "32px",
            background: "transparent",
            border: "none",
            color: "#ccc",
            cursor: "pointer",
            zIndex: 10001,
          }}
          aria-label="Next image"
        >
          ›
        </button>
      )}
    </div>,
    document.body
  );

}
